// utilities for current study data store

// simple fifo cache for vtkImageData objects with metadata
import type vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import type { Bounds } from '@kitware/vtk.js/types';

import type { ImageMetadata } from '../../types/image';

import { mat3, mat4, vec3 } from 'gl-matrix';

import { defaultLPSDirections /* , getLPSDirections */ } from '../../utils/lps';

export interface ImageAndMetaData {
  imageData: vtkImageData;
  metaData: ImageMetadata;
}

type DeletionCallback = (item: string) => void;

export class VtkImageAndMetaDataCache {
  private cache: Record<string, ImageAndMetaData>;
  private order: string[];
  private onDelete: DeletionCallback;
  private maxSize: number;

  constructor(onDelete: DeletionCallback, maxSize: number = 10) {
    this.cache = {};
    this.order = [] as string[];
    this.onDelete = onDelete;
    this.maxSize = maxSize;
  }

  add(key: string, data: ImageAndMetaData): void {
    if (this.cache[key]) {
      this.remove(key);
    }
    if (this.order.length >= this.maxSize) {
      const oldestKey = this.order[0];
      if (oldestKey) {
        this.remove(oldestKey);
      }
    }

    this.cache[key] = data;
    this.order.push(key);
  }

  get(key: string): ImageAndMetaData {
    // | null {
    // console.log('CacheSize: ', this.size());
    return this.cache[key]; // || null;
  }

  remove(key: string): void {
    if (this.cache[key]) {
      this.onDelete(key);
      delete this.cache[key];
      const index = this.order.indexOf(key);
      if (index > -1) {
        this.order.splice(index, 1);
      }
    }
  }

  clear(): void {
    Object.keys(this.cache).forEach((key) => {
      this.onDelete(key);
    });
    this.cache = {};
    this.order = [] as string[];
  }

  size(): number {
    return this.order.length;
  }

  reset(): void {
    this.cache = {};
    this.order = [] as string[];
  }
}

export const defaultImageMetadata = () => ({
  name: '(none)',
  orientation: mat3.create(),
  lpsOrientation: defaultLPSDirections(),
  spacing: vec3.fromValues(1, 1, 1),
  origin: vec3.create(),
  dimensions: vec3.fromValues(1, 1, 1),
  worldBounds: [0, 1, 0, 1, 0, 1] as Bounds,
  worldToIndex: mat4.create(),
  indexToWorld: mat4.create(),
});
